import {
  getDevicePixelRatio,
  getRuntimeDocument,
  getRuntimeWindow,
  invertMatrix,
  Matrix2D,
  Point,
  transformPoint,
} from '@imagise/shared';
import { TMat2D, TCornerPoint } from '../object';
import { CommonMethods } from './CommonMethods';
import { StaticCanvasDOMManager, CANVAS_DATA_ATTRIBUTE, CanvasSize } from './dom-managers';
import { CanvasObject, CanvasRenderContext } from './types/canvas-object';
import { StaticCanvasEvents, RenderRequestReason, RenderEvent } from './types/static-canvas-events';
import { StaticCanvasOptions } from './types/static-canvas-options';
import { createCollectionMixin } from './create-collection-mixin';

const IDENTITY_MATRIX: TMat2D = [1, 0, 0, 1, 0, 0];
const FRAME_DURATION = 16;

const getDomDocument = (): Document | undefined => {
  try {
    return getRuntimeDocument();
  } catch {
    return undefined;
  }
};

const getDomWindow = (): (Window & typeof globalThis) | undefined => {
  try {
    return getRuntimeWindow();
  } catch {
    return undefined;
  }
};

const requestFrame = (cb: () => void): number => {
  const runtimeWindow = getDomWindow();
  if (runtimeWindow && typeof runtimeWindow.requestAnimationFrame === 'function') {
    return runtimeWindow.requestAnimationFrame(() => cb());
  }
  return setTimeout(cb, FRAME_DURATION) as unknown as number;
};

const cancelFrame = (handle: number): void => {
  const runtimeWindow = getDomWindow();
  if (runtimeWindow && typeof runtimeWindow.cancelAnimationFrame === 'function') {
    runtimeWindow.cancelAnimationFrame(handle);
    return;
  }
  clearTimeout(handle);
};

const DEFAULT_OPTIONS: Required<
  Pick<
    StaticCanvasOptions,
    | 'width'
    | 'height'
    | 'backgroundColor'
    | 'overlayColor'
    | 'renderOnAddRemove'
    | 'skipOffscreen'
    | 'enableRetinaScaling'
    | 'imageSmoothingEnabled'
  >
> = {
  width: 0,
  height: 0,
  backgroundColor: 'transparent',
  overlayColor: 'transparent',
  renderOnAddRemove: true,
  skipOffscreen: true,
  enableRetinaScaling: true,
  imageSmoothingEnabled: true,
};

function cloneMatrix(matrix: TMat2D): TMat2D {
  return [...matrix] as TMat2D;
}

/**
 * SSR-friendly StaticCanvas implementation
 */
export class StaticCanvas<EventSpec extends StaticCanvasEvents = StaticCanvasEvents>
  extends createCollectionMixin(CommonMethods<StaticCanvasEvents>)
  implements StaticCanvasOptions
{
  declare fire: CommonMethods<EventSpec>['fire'];
  declare on: CommonMethods<EventSpec>['on'];
  declare once: CommonMethods<EventSpec>['once'];
  declare off: CommonMethods<EventSpec>['off'];
  width = DEFAULT_OPTIONS.width;
  height = DEFAULT_OPTIONS.height;

  backgroundColor = DEFAULT_OPTIONS.backgroundColor;
  overlayColor = DEFAULT_OPTIONS.overlayColor;
  backgroundImage?: CanvasObject;
  overlayImage?: CanvasObject;

  renderOnAddRemove = DEFAULT_OPTIONS.renderOnAddRemove;
  skipOffscreen = DEFAULT_OPTIONS.skipOffscreen;
  enableRetinaScaling = DEFAULT_OPTIONS.enableRetinaScaling;
  imageSmoothingEnabled = DEFAULT_OPTIONS.imageSmoothingEnabled;

  viewportTransform: TMat2D = cloneMatrix(IDENTITY_MATRIX);
  vptCoords: TCornerPoint = {
    tl: new Point(0, 0),
    tr: new Point(0, 0),
    br: new Point(0, 0),
    bl: new Point(0, 0),
  };

  private _devicePixelRatio?: number;
  private _pixelRatio = 1;
  private _nextRenderHandle?: number;
  private _disposed = false;
  private _offset = { left: 0, top: 0 };

  declare elements: StaticCanvasDOMManager;

  constructor(element?: string | HTMLCanvasElement, options: StaticCanvasOptions = {}) {
    super();
    this.applyOptions(options);
    this.initElements(element, options);
    this.syncCanvasSize();
    this.updateViewportCoordinates();
    this.requestRenderAll('initial');
  }

  /**
   * Returns underlying lower canvas element if available.
   */
  get lowerCanvasEl(): HTMLCanvasElement | undefined {
    return this.getElement();
  }

  /**
   * Returns the rendering context of the lower canvas if available.
   */
  get contextContainer(): CanvasRenderingContext2D | null | undefined {
    return this.getContext();
  }

  /**
   * Returns underlying canvas element if available.
   */
  getElement(): HTMLCanvasElement | undefined {
    const el = this.elements?.lower?.el;
    return el && typeof el.getContext === 'function' ? el : undefined;
  }

  /**
   * Returns the 2d context, if initialized.
   */
  getContext(): CanvasRenderingContext2D | null | undefined {
    const lower = this.elements?.lower;
    if (!lower) {
      return undefined;
    }
    if (!lower.ctx && typeof lower.el.getContext === 'function') {
      lower.ctx = lower.el.getContext('2d');
    }
    return lower.ctx;
  }

  /**
   * Attaches canvas to an existing DOM element.
   */
  attachToElement(element: string | HTMLCanvasElement): void {
    const runtimeDocument = getDomDocument();
    if (!runtimeDocument) {
      return;
    }
    const canvas =
      typeof element === 'string' ? (runtimeDocument.getElementById(element) as HTMLCanvasElement | null) : element;
    if (!canvas) {
      throw new Error('StaticCanvas: canvas element not found');
    }
    if (canvas.hasAttribute('data-static-canvas') || canvas.hasAttribute(CANVAS_DATA_ATTRIBUTE)) {
      throw new Error('StaticCanvas: canvas element is already managed by another instance');
    }
    this.cleanupElements();
    this.initElements(canvas);
    this.syncCanvasSize();
  }

  /**
   * Sets canvas dimensions in logical pixels.
   */
  setDimensions(size: Partial<CanvasSize>): void {
    const nextWidth = size.width ?? this.width;
    const nextHeight = size.height ?? this.height;
    const changed = nextWidth !== this.width || nextHeight !== this.height;
    this.width = nextWidth;
    this.height = nextHeight;
    if (changed) {
      this.syncCanvasSize();
      this.calcOffset();
      this.updateViewportCoordinates();
      this.fire(
        'canvas:resized' as keyof EventSpec,
        {
          width: this.width,
          height: this.height,
        } as EventSpec[keyof EventSpec]
      );
      this.requestRenderAll('resize');
    }
  }

  /**
   * Updates viewport transform.
   */
  setViewportTransform(transform: TMat2D): void {
    this.viewportTransform = cloneMatrix(transform);
    this.updateViewportCoordinates();
    this.requestRenderAll('manual');
  }

  /**
   * Sets background color and schedules render.
   */
  setBackgroundColor(color: string): void {
    if (this.backgroundColor !== color) {
      this.backgroundColor = color;
      this.requestRenderAll('manual');
    }
  }

  /**
   * Sets overlay color and schedules render.
   */
  setOverlayColor(color: string): void {
    if (this.overlayColor !== color) {
      this.overlayColor = color;
      this.requestRenderAll('manual');
    }
  }

  /**
   * Clears canvas objects and fires canvas:cleared event.
   */
  clear(): void {
    // Upstream semantics: clear resets background/overlay state and fires
    // canvas:cleared unconditionally, even when no objects are present.
    const objects = [...this._objects];
    if (objects.length) {
      this.remove(...objects);
    }
    this.backgroundImage = undefined;
    this.overlayImage = undefined;
    this.backgroundColor = DEFAULT_OPTIONS.backgroundColor;
    this.overlayColor = DEFAULT_OPTIONS.overlayColor;
    this.fire(
      'canvas:cleared' as keyof EventSpec,
      {
        timestamp: Date.now(),
      } as EventSpec[keyof EventSpec]
    );
    this.requestRenderAll('manual');
  }

  /**
   * Cancel pending render requests.
   */
  cancelRenderAll(): void {
    if (!this._nextRenderHandle) {
      return;
    }
    cancelFrame(this._nextRenderHandle);
    this._nextRenderHandle = undefined;
  }

  /**
   * Requests rendering on next animation frame (or timeout on SSR).
   */
  requestRenderAll(reason: RenderRequestReason = 'manual'): void {
    if (this._disposed || this._nextRenderHandle) {
      return;
    }
    this.fire(
      'render:requested' as keyof EventSpec,
      {
        reason,
      } as EventSpec[keyof EventSpec]
    );
    this._nextRenderHandle = requestFrame(() => {
      this._nextRenderHandle = undefined;
      this.renderAll();
    });
  }

  /**
   * Renders canvas immediately.
   */
  renderAll(): void {
    if (this._disposed) {
      return;
    }
    const ctx = this.getContext();
    const event = this.createRenderEvent(ctx ?? undefined);
    this.fire('before:render' as keyof EventSpec, event as EventSpec[keyof EventSpec]);
    if (!ctx) {
      return;
    }

    ctx.save();
    this.applyRetinaTransform(ctx);
    this.clearContext(ctx);
    this.drawBackground(ctx);
    ctx.save();
    this.applyViewportTransform(ctx);
    this.drawObjects(ctx);
    this.renderViewportExtras(ctx);
    ctx.restore();
    this.drawOverlay(ctx);
    this.renderOverlayExtras(ctx);
    ctx.restore();

    this.fire(
      'after:render' as keyof EventSpec,
      {
        ...event,
        context: ctx,
      } as EventSpec[keyof EventSpec]
    );
  }

  /**
   * Serializes canvas into plain object.
   */
  toObject(): Record<string, unknown> {
    return {
      width: this.width,
      height: this.height,
      backgroundColor: this.backgroundColor,
      overlayColor: this.overlayColor,
      viewportTransform: cloneMatrix(this.viewportTransform),
      objects: this._objects.map(object => (typeof object.toObject === 'function' ? object.toObject() : {})),
    };
  }

  /**
   * Serializes canvas to JSON string.
   */
  toJSON(): string {
    return JSON.stringify(this.toObject());
  }

  /**
   * Cleans up DOM references.
   */
  dispose(): void {
    this.cancelRenderAll();
    // Mirror upstream destroy(): release object event listeners and drop
    // references so a retained canvas does not keep the whole scene alive.
    const objects = [...this._objects];
    this._objects.length = 0;
    objects.forEach(object => (object as { dispose?: () => void }).dispose?.());
    this.backgroundImage = undefined;
    this.overlayImage = undefined;
    this.cleanupElements();
    this._disposed = true;
  }

  public override _onObjectAdded(object: CanvasObject): void {
    const index = this._objects.indexOf(object);
    this.fire(
      'object:added' as keyof EventSpec,
      {
        target: object,
        index,
      } as EventSpec[keyof EventSpec]
    );
    if (this.renderOnAddRemove) {
      this.requestRenderAll('add-remove');
    }
  }

  override _onObjectRemoved(object: CanvasObject, index = -1): void {
    this.fire(
      'object:removed' as keyof EventSpec,
      {
        target: object,
        index,
      } as EventSpec[keyof EventSpec]
    );
    if (this.renderOnAddRemove) {
      this.requestRenderAll('add-remove');
    }
  }

  override _onStackOrderChanged(object: CanvasObject): void {
    const index = this._objects.indexOf(object);
    this.fire(
      'object:stack:changed' as keyof EventSpec,
      {
        target: object,
        index,
      } as EventSpec[keyof EventSpec]
    );
    if (this.renderOnAddRemove) {
      this.requestRenderAll('stack-order');
    }
  }

  private applyOptions(options: StaticCanvasOptions): void {
    const {
      width,
      height,
      backgroundColor,
      overlayColor,
      backgroundImage,
      overlayImage,
      renderOnAddRemove,
      skipOffscreen,
      enableRetinaScaling,
      imageSmoothingEnabled,
      viewportTransform,
      devicePixelRatio,
    } = options;

    if (typeof width === 'number' || typeof height === 'number') {
      this.setDimensions({
        width: width ?? this.width,
        height: height ?? this.height,
      });
    }

    if (typeof backgroundColor === 'string') {
      this.backgroundColor = backgroundColor;
    }
    if (typeof overlayColor === 'string') {
      this.overlayColor = overlayColor;
    }
    if (backgroundImage) {
      this.backgroundImage = backgroundImage;
    }
    if (overlayImage) {
      this.overlayImage = overlayImage;
    }
    if (typeof renderOnAddRemove === 'boolean') {
      this.renderOnAddRemove = renderOnAddRemove;
    }
    if (typeof skipOffscreen === 'boolean') {
      this.skipOffscreen = skipOffscreen;
    }
    if (typeof enableRetinaScaling === 'boolean') {
      this.enableRetinaScaling = enableRetinaScaling;
    }
    if (typeof imageSmoothingEnabled === 'boolean') {
      this.imageSmoothingEnabled = imageSmoothingEnabled;
    }
    if (viewportTransform) {
      this.viewportTransform = cloneMatrix(viewportTransform);
    }
    if (typeof devicePixelRatio === 'number') {
      this._devicePixelRatio = devicePixelRatio;
    }
  }

  protected getRetinaScaling(): number {
    if (!this.enableRetinaScaling) {
      return 1;
    }
    if (typeof this._devicePixelRatio === 'number') {
      return this._devicePixelRatio;
    }
    return getDevicePixelRatio();
  }

  private syncCanvasSize(): void {
    const ctx = this.getContext();
    if (!this.elements || !ctx) {
      return;
    }
    const ratio = this.getRetinaScaling();
    this._pixelRatio = ratio;
    const logicalWidth = Math.max(this.width, 0);
    const logicalHeight = Math.max(this.height, 0);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    this.elements.setDimensions({ width: logicalWidth, height: logicalHeight }, ratio);
    this.elements.setCSSDimensions({
      width: logicalWidth,
      height: logicalHeight,
    });
    ctx.imageSmoothingEnabled = this.imageSmoothingEnabled;
  }

  private updateViewportCoordinates(): void {
    // The visible scene region is the canvas rectangle mapped through the
    // INVERSE viewport transform (matches upstream calcViewportBoundaries).
    const inverse = invertMatrix(this.viewportTransform as Matrix2D);
    const corners = [
      new Point(0, 0),
      new Point(this.width, 0),
      new Point(this.width, this.height),
      new Point(0, this.height),
    ].map(point => transformPoint(point, inverse));
    this.vptCoords = {
      tl: corners[0],
      tr: corners[1],
      br: corners[2],
      bl: corners[3],
    };
  }

  private applyRetinaTransform(ctx: CanvasRenderingContext2D): void {
    ctx.setTransform(this._pixelRatio, 0, 0, this._pixelRatio, 0, 0);
    ctx.imageSmoothingEnabled = this.imageSmoothingEnabled;
  }

  private applyViewportTransform(ctx: CanvasRenderingContext2D): void {
    const [a, b, c, d, e, f] = this.viewportTransform;
    ctx.transform(a, b, c, d, e, f);
  }

  private clearContext(ctx: CanvasRenderingContext2D): void {
    ctx.clearRect(0, 0, this.width, this.height);
  }

  protected initElements(element?: string | HTMLCanvasElement, _options?: StaticCanvasOptions): void {
    this.elements = new StaticCanvasDOMManager(element);
  }

  protected calcOffset(): { left: number; top: number } {
    this._offset = this.elements?.calcOffset() ?? { left: 0, top: 0 };
    return this._offset;
  }

  private cleanupElements(): void {
    if (!this.elements) {
      return;
    }
    this.elements.cleanupDOM({ width: this.width, height: this.height });
    this.elements.dispose();
    // @ts-expect-error disposing
    delete this.elements;
  }

  private drawBackground(ctx: CanvasRenderingContext2D): void {
    if (this.backgroundColor) {
      ctx.save();
      ctx.fillStyle = this.backgroundColor;
      ctx.fillRect(0, 0, this.width, this.height);
      ctx.restore();
    }
    if (this.backgroundImage) {
      ctx.save();
      this.backgroundImage.render(ctx, this.createRenderContext());
      ctx.restore();
    }
  }

  private drawOverlay(ctx: CanvasRenderingContext2D): void {
    if (this.overlayImage) {
      ctx.save();
      this.overlayImage.render(ctx, this.createRenderContext());
      ctx.restore();
    }
    if (this.overlayColor) {
      ctx.save();
      ctx.fillStyle = this.overlayColor;
      ctx.fillRect(0, 0, this.width, this.height);
      ctx.restore();
    }
  }

  private drawObjects(ctx: CanvasRenderingContext2D): void {
    const renderContext = this.createRenderContext();
    this._objects.forEach(object => {
      const renderable = object as CanvasObject;

      if (renderable.visible === false) {
        return;
      }

      if (
        this.skipOffscreen &&
        typeof renderable.shouldRenderInViewport === 'function' &&
        !renderable.shouldRenderInViewport(renderContext.viewportTransform, this.skipOffscreen)
      ) {
        return;
      }

      renderable.render(ctx, renderContext);
    });
  }

  private createRenderContext(): CanvasRenderContext {
    return {
      viewportTransform: cloneMatrix(this.viewportTransform),
      retinaScaling: this._pixelRatio,
      width: this.width,
      height: this.height,
      skipOffscreen: this.skipOffscreen,
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected renderViewportExtras(ctx: CanvasRenderingContext2D): void {
    // Subclasses can override to render interactive objects inside viewport space
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected renderOverlayExtras(ctx: CanvasRenderingContext2D): void {
    // Subclasses can override to draw screen-space overlays
  }

  private createRenderEvent(context?: CanvasRenderingContext2D | null): RenderEvent {
    return {
      timestamp: Date.now(),
      width: this.width,
      height: this.height,
      context,
    };
  }
}
